import { Message } from '../web.common'
import { getTranslatedText } from '../languages';
import features from '../../settings/features';

const stacktrace = require('stacktrace-js');
const configReader = require('../config');

export function readArgument(name) {
  return process.argv
    .map(x => x.match(/(.*)=(.*)/))
    .filter(x => !!x)
    .filter(x => x[1] === name)
    .map(x => x[2])[0];
}

function serializeErrorDetails(error) {
  JSON.stringify({
    Message: error.message,
    File: error.filename,
    Line: error.lineno,
    Column: error.colno,
    'Error object': error.error
  });
}

async function readStackFromError(error) {
  try {
    const stack = await stacktrace.fromError(error);
    if (!stack) {
      return null;
    }

    return stack.join('\n    at ');
  }
  catch (e) {
    return null;
  }
}

export async function getDetailedError(error) {
  return readStackFromError(error) || serializeErrorDetails(error);
}

export function getApiErrorMessage(error, language) {
  if (!error || !language) {
    console.error('Missing parameters in getApiErrorMessage!');
    return false;
  }

  const { payload } = error;

  let displayedErrorMessage = getTranslatedText('error-general', 'general/messages', language);

  if (payload) {
    const {
      message,
      translatedMessage
    } = payload;

    displayedErrorMessage = translatedMessage || message;
  }

  return new Message('error', displayedErrorMessage);
}

export async function asyncForEach(array, callBack) {
  for (let i = 0, arrayLen = array.length; i < arrayLen; i += 1) {
    await callBack(array[i], i, array);
  }
}

export function focusFirstElement(
  parentDomElement,
  focusableElements = [
    'button',
    'input',
    'select',
    'textarea',
    '[tabindex]:not([tabindex="-1"])'
  ]
) {
  const firstFocusableChild = parentDomElement.querySelector(focusableElements.join(', '));
  firstFocusableChild.focus();
}

export function mapObject(inputObject, callback) {
  const outputObject = {};

  Object.entries(inputObject).forEach(entry => {
    const {
      0: newKey,
      1: newValue
    } = callback(entry);

    outputObject[newKey] = newValue;
  });

  return outputObject;
}

export function cloneObject(objectToClone, objectToAssign = {}, propsToExclude = []) {
  const outputObject = Object.assign(objectToAssign, objectToClone);

  if (propsToExclude.length > 0) {
    propsToExclude.forEach(excludedProp => delete outputObject[excludedProp]);
  }

  return outputObject;
}

export function getStorableDateString(dateObject) {
  if (Number.isNaN(dateObject)) {
    console.error('Invalid date object passed!', dateObject);
    return false;
  }

  const month = dateObject.getMonth() + 1;
  const day = dateObject.getDate();
  return [dateObject.getFullYear(), (month > 9 ? '' : '0') + month, (day > 9 ? '' : '0') + day].join('-');
}

export function validatePropTypeDate(props, propName, componentName, isRequired) {
  const value = props[propName];
  const valueType = typeof (value);

  if (isRequired && !value) {
    return new Error(`Error: missing required prop "${propName}" - in ${componentName}`);
  }

  if (value && Object.prototype.toString.call(value) !== '[object Date]') {
    return new Error(`Error: "${propName}" value should be a Date object, "${
      valueType === 'object' ? Object.prototype.toString.call(value) : valueType
    }" was provided - in ${componentName}`);
  }
}

export function validatePropTypeInterval(
  props,
  propName,
  componentName,
  minValue,
  maxValue,
  isRequired
) {
  const value = props[propName];
  const valueType = typeof (value);

  if (isRequired && !value) {
    return new Error(`Error: missing required prop "${propName}" - in ${componentName}`);
  }

  if (value) {
    if (valueType !== 'number') {
      return new Error(`Error: "${propName}" value should be a number, "${
        valueType === 'object' ? Object.prototype.toString.call(value) : valueType
      }" was provided - in ${componentName}`);
    }

    if (value < minValue || value > maxValue) {
      return new Error(`Invalid "${propName}" prop value, supplied: "${value}" - in ${componentName}`);
    }
  }
}

export function manageEventListener(targetDOMElement, eventName, action, callback) {
  switch (action) {
    case 'add':
      targetDOMElement.addEventListener(eventName, callback);
      break;

    case 'remove':
      targetDOMElement.removeEventListener(eventName, callback);
      break;

    default:
      console.error('"action" is missing or invalid');
      return false;
  }
}

export function onClickOutsideElement(event, elementSelector, callback) {
  if (!event.target.closest(elementSelector)) {
    callback(event);
  }
}

export function getNestedValueByPath(object, nestingPathWithSlashes) {
  const parentProps = nestingPathWithSlashes.split('/').filter(key => key !== '');
  const nestedValue = parentProps.reduce((xs, x) => ((xs && xs[x]) ? xs[x] : null), object);

  if (!nestedValue) {
    throw new Error(
      `ERROR: no nested value could be found in the provided nesting path (${
        nestingPathWithSlashes
      })`
    );
  }

  return nestedValue;
}

export function initFacebook() {
  FB.init({
    appId: configReader.get().facebookAppId,
    autoLogAppEvents: true,
    xfbml: true,
    version: 'v3.3'
  });
}

export async function accessFacebook() {
  return new Promise((resolve, reject) => {
    try {
      FB.login(({ authResponse }) => {
        const {
          accessToken,
          userID: facebookId
        } = authResponse;

        if (authResponse && accessToken && facebookId) {
          resolve({
            accessToken,
            facebookId
          });
        }
        else {
          reject(new Error('error1'));
        }
      }, { scope: 'email,public_profile' });
    }
    catch (e) {
      reject(new Error('error2'));
    }
  });
}

export function transformFirstLetter(string, mode) {
  let firstLetter = string.charAt(0);

  switch (mode) {
    case 'upper':
      firstLetter = firstLetter.toUpperCase();
      break;

    case 'lower':
      firstLetter = firstLetter.toLowerCase();
      break;

    default:
      throw new Error(`Invalid letter transformation mode "${mode}"`);
  }

  return `${firstLetter}${string.slice(1)}`;
}

export function isFeatureAllowed(featureName) {
  const featureSettings = features.find(({ feature }) => feature === featureName);

  if (!featureSettings) {
    throw new Error(`Feature "${featureName}" could not be found`);
  }

  return featureSettings[configReader.get().mode];
}

export function transformIntoCamelCase(string, separator = '-') {
  const mainParamType = typeof (string);

  if (mainParamType !== 'string') {
    throw new Error(
      `transformIntoCamelCase expects the first parameter to be string,
      "${mainParamType}" was passed`
    );
  }

  const explodedString = string.split(separator);
  const transformedWords = explodedString.map((word, index) => (
    index !== 0 ? transformFirstLetter(word, 'upper') : word
  ));

  return transformedWords.join('');
}

export function getQueryStringParameters(search) {
  const parameters = search.split('?')[1] || '';

  return parameters.split('&')
    .map(x => x.split('='))
    .reduce((result, current) => {
      const dictionary = result;
      const [key, val] = current;
      dictionary[key] = decodeURIComponent(val);

      return dictionary;
    }, {});
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Compares two labels the way a person reads them: "Bane 2" before "Bane 10",
 * case ignored. Court names are mostly a hall and a number, so plain string order
 * puts every "Bane 1x" between "Bane 1" and "Bane 2".
 */
export const naturalCompare = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });
